// src/app/core/services/scroll-state.service.ts (FULL DROP-IN)

import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, NgZone, PLATFORM_ID } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class ScrollStateService {
  private readonly TOP_EPS_PX = 5;

  private readonly _atTop = new BehaviorSubject<boolean>(true);
  /** true when the active scroll container is near the top */
  readonly atTop$ = this._atTop.asObservable();

  private readonly isBrowser: boolean;

  // “Candidate” scroll container (desktop: main-content). On mobile, document scroll is used.
  private scrollerEl: HTMLElement | null = null;

  // Track which source most recently scrolled (helps during transitions).
  private lastSource: 'window' | 'element' = 'window';

  private rafId: number | null = null;

  private winScrollHandler?: () => void;
  private elScrollHandler?: () => void;
  private resizeHandler?: () => void;

  constructor(
    @Inject(PLATFORM_ID) pid: Object,
    @Inject(DOCUMENT) private readonly doc: Document,
    private readonly zone: NgZone,
  ) {
    this.isBrowser = isPlatformBrowser(pid);

    if (!this.isBrowser) return;

    this.zone.runOutsideAngular(() => {
      this.winScrollHandler = () => {
        this.lastSource = 'window';
        this.scheduleUpdate();
      };

      this.resizeHandler = () => {
        // CSS can switch the active scroll container (element <-> document)
        this.lastSource = this.getPreferredSource();
        this.scheduleUpdate();
      };

      window.addEventListener('scroll', this.winScrollHandler, {
        passive: true,
      });
      window.addEventListener('resize', this.resizeHandler, { passive: true });
    });

    // Initial read
    queueMicrotask(() => this.updateNow());
  }

  /**
   * Register the main scroll container (desktop uses this element; mobile uses document scroll).
   * Safe to call multiple times; it will only rewire listeners when the element instance changes.
   */
  setFrom(el: HTMLElement | null): void {
    if (!this.isBrowser) return;

    // Treat this as “register scroller”, not “handle scroll tick”
    if (el === this.scrollerEl) return;

    this.detachElementListener();
    this.scrollerEl = el;

    if (this.scrollerEl) this.attachElementListener(this.scrollerEl);

    this.lastSource = this.getPreferredSource();
    this.scheduleUpdate();
  }

  // -----------------------
  // Internals
  // -----------------------

  private attachElementListener(el: HTMLElement): void {
    this.zone.runOutsideAngular(() => {
      this.elScrollHandler = () => {
        this.lastSource = 'element';
        this.scheduleUpdate();
      };

      el.addEventListener('scroll', this.elScrollHandler, { passive: true });
    });
  }

  private detachElementListener(): void {
    if (!this.scrollerEl || !this.elScrollHandler) return;

    this.scrollerEl.removeEventListener(
      'scroll',
      this.elScrollHandler as EventListener,
    );
    this.elScrollHandler = undefined;
  }

  private scheduleUpdate(): void {
    if (!this.isBrowser) return;

    if (this.rafId != null) cancelAnimationFrame(this.rafId);

    this.rafId = requestAnimationFrame(() => {
      this.rafId = null;
      this.zone.run(() => this.updateNow());
    });
  }

  private updateNow(): void {
    if (!this.isBrowser) return;

    // If the scroll container switches due to responsive CSS,
    // prefer the container that *can* actually scroll right now.
    const preferred = this.getPreferredSource();
    const source = this.lastSource === preferred ? this.lastSource : preferred;

    const top =
      source === 'element'
        ? (this.scrollerEl?.scrollTop ?? 0)
        : this.getWindowScrollTop();

    const atTop = top < this.TOP_EPS_PX;
    if (this._atTop.value !== atTop) this._atTop.next(atTop);
  }

  private getPreferredSource(): 'window' | 'element' {
    if (!this.scrollerEl) return 'window';
    return this.isElementScrollContainer(this.scrollerEl)
      ? 'element'
      : 'window';
  }

  private isElementScrollContainer(el: HTMLElement): boolean {
    // Mobile CSS sets overflow: visible, so this returns false and we use window/document scroll.
    const s = getComputedStyle(el);
    const oy = s.overflowY;
    return /(auto|scroll|overlay)/.test(oy);
  }

  private getWindowScrollTop(): number {
    return (
      window.scrollY ||
      this.doc.documentElement.scrollTop ||
      this.doc.body.scrollTop ||
      0
    );
  }
}
